import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { User, CreditCard, ShieldCheck, ShieldAlert, ShieldX, CheckCircle2, Loader2, AlertTriangle, Clock } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function Field({ label, value, mono }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.1em] text-gray-600 mb-1">{label}</div>
      <div className={`text-sm text-gray-200 ${mono ? "font-mono tracking-wider" : ""}`}>{value || "—"}</div>
    </div>
  );
}

export default function PartnerProfile() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [kycStatus, setKycStatus] = useState(null);
  const [verifyingPan, setVerifyingPan] = useState(false);
  const [verifyingBank, setVerifyingBank] = useState(false);

  const load = useCallback(async () => {
    try {
      const [res, kycRes] = await Promise.all([
        axios.get(`${API}/api/sm/partners/portal/me`, { withCredentials: true }),
        axios.get(`${API}/api/sm/partners/portal/kyc/status`, { withCredentials: true })
          .catch(() => ({ data: null })),
      ]);
      setProfile(res.data);
      setKycStatus(kycRes.data);
      setForm({
        phone: res.data.phone || "",
        address: res.data.address || "",
        state: res.data.state || "",
        bank_name: res.data.bank_name || "",
        bank_ifsc: res.data.bank_ifsc || "",
        bank_account_holder: res.data.bank_account_holder || "",
        gst_number: res.data.gst_number || "",
      });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleVerifyPAN = async () => {
    setVerifyingPan(true);
    try {
      const r = await axios.post(`${API}/api/sm/partners/portal/kyc/verify-pan`, {}, { withCredentials: true });
      if (r.data.valid) {
        const auto = r.data.auto_approval;
        if (auto?.approved) toast.success("PAN verified — KYC auto-approved! Your account is now active.");
        else toast.success(`PAN verified${r.data.name_on_pan ? ` — ${r.data.name_on_pan}` : ""}`);
        await load();
      } else {
        toast.error(r.data.error || "PAN verification failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "PAN verification failed");
    } finally { setVerifyingPan(false); }
  };

  const handleVerifyBank = async () => {
    setVerifyingBank(true);
    try {
      const r = await axios.post(`${API}/api/sm/partners/portal/kyc/verify-bank`, {}, { withCredentials: true });
      if (r.data.valid) {
        const auto = r.data.auto_approval;
        if (auto?.approved) toast.success("Bank verified — KYC auto-approved! Your account is now active.");
        else if (auto?.reason) toast.success(`Bank verified. ${auto.reason}`);
        else toast.success("Bank account verified successfully");
        await load();
      } else {
        toast.error(r.data.error || "Bank verification failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bank verification failed");
    } finally { setVerifyingBank(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/api/sm/partners/portal/me`, form, { withCredentials: true });
      toast.success("Profile updated");
      setEditing(false);
      await load();
    } catch (e) {
      toast.error("Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="space-y-4">
      {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-24 bg-white/5 rounded-xl animate-pulse" />)}
    </div>
  );

  const overallKycDone    = kycStatus?.kyc_status === "verified" || kycStatus?.kyc_status === "active";
  const kycRejected       = kycStatus?.kyc_status === "rejected";
  const kycAdminReview    = kycStatus?.kyc_status === "admin_review";
  const panVerified       = kycStatus?.pan_verified  || false;
  const bankVerified      = kycStatus?.bank_verified || false;

  const headerBadge = overallKycDone
    ? { label: "KYC Verified",       cls: "text-emerald-400 bg-emerald-400/10",  icon: ShieldCheck }
    : kycRejected
    ? { label: "KYC Rejected",       cls: "text-red-400 bg-red-400/10",           icon: ShieldX }
    : kycAdminReview
    ? { label: "Under Review",       cls: "text-amber-400 bg-amber-400/10",       icon: Clock }
    : { label: "KYC Pending",        cls: "text-amber-400 bg-amber-400/10",       icon: ShieldAlert };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="partner-profile-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-serif text-3xl text-white font-light">Profile & Bank Details</h1>
          <p className="text-sm text-gray-500 mt-1">Keep your KYC and payment details up to date</p>
        </div>
        <span className={`text-xs px-2.5 py-1 rounded-full font-medium flex items-center gap-1.5 ${headerBadge.cls}`}>
          <headerBadge.icon className="w-3 h-3" />
          {headerBadge.label}
        </span>
      </div>

      {/* Personal Info */}
      <div className="bg-[#0f0f0f] border border-white/10 rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-5 pb-4 border-b border-white/5">
          <User className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
          <span className="text-[11px] uppercase tracking-[0.15em] text-gray-500">Personal Information</span>
        </div>
        <div className="grid grid-cols-2 gap-5">
          <Field label="Full Name" value={profile?.name} />
          <Field label="Email" value={profile?.email} />
          {editing ? (
            <div>
              <div className="text-[11px] uppercase tracking-[0.1em] text-gray-600 mb-1">Phone</div>
              <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#D4AF37]/50" />
            </div>
          ) : (
            <Field label="Phone" value={profile?.phone} />
          )}
          <Field label="PAN Number" value={profile?.pan_number} mono />
          {editing ? (
            <div className="col-span-2">
              <div className="text-[11px] uppercase tracking-[0.1em] text-gray-600 mb-1">Address</div>
              <input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#D4AF37]/50" />
            </div>
          ) : (
            <Field label="Address" value={profile?.address} />
          )}
          {editing ? (
            <div>
              <div className="text-[11px] uppercase tracking-[0.1em] text-gray-600 mb-1">State</div>
              <input value={form.state} onChange={e => setForm(f => ({ ...f, state: e.target.value }))}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#D4AF37]/50" />
            </div>
          ) : (
            <Field label="State" value={profile?.state} />
          )}
          {editing ? (
            <div>
              <div className="text-[11px] uppercase tracking-[0.1em] text-gray-600 mb-1">GST Number (optional)</div>
              <input value={form.gst_number} onChange={e => setForm(f => ({ ...f, gst_number: e.target.value }))}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#D4AF37]/50" />
            </div>
          ) : (
            <Field label="GST Number" value={profile?.gst_number} />
          )}
        </div>
      </div>

      {/* Bank Details */}
      <div className="bg-[#0f0f0f] border border-white/10 rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-5 pb-4 border-b border-white/5">
          <CreditCard className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
          <span className="text-[11px] uppercase tracking-[0.15em] text-gray-500">Bank Details</span>
        </div>
        <div className="grid grid-cols-2 gap-5">
          <Field label="Account Number" value={profile?.bank_account_masked || "****..."} mono />
          {editing ? (
            <>
              <div>
                <div className="text-[11px] uppercase tracking-[0.1em] text-gray-600 mb-1">IFSC Code</div>
                <input value={form.bank_ifsc} onChange={e => setForm(f => ({ ...f, bank_ifsc: e.target.value }))}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white font-mono focus:outline-none focus:border-[#D4AF37]/50" />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.1em] text-gray-600 mb-1">Bank Name</div>
                <input value={form.bank_name} onChange={e => setForm(f => ({ ...f, bank_name: e.target.value }))}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#D4AF37]/50" />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.1em] text-gray-600 mb-1">Account Holder Name</div>
                <input value={form.bank_account_holder} onChange={e => setForm(f => ({ ...f, bank_account_holder: e.target.value }))}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#D4AF37]/50" />
              </div>
            </>
          ) : (
            <>
              <Field label="IFSC Code" value={profile?.bank_ifsc} mono />
              <Field label="Bank Name" value={profile?.bank_name} />
              <Field label="Account Holder" value={profile?.bank_account_holder} />
            </>
          )}
        </div>
      </div>

      {/* KYC Verification */}
      <div className="bg-[#0f0f0f] border border-white/10 rounded-2xl p-6" data-testid="partner-kyc-section">
        <div className="flex items-center gap-2 mb-5 pb-4 border-b border-white/5">
          <ShieldCheck className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
          <span className="text-[11px] uppercase tracking-[0.15em] text-gray-500">KYC Verification</span>
          {kycStatus?.live_mode && (
            <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">Live — Perfios</span>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* PAN */}
          <div className="bg-white/3 border border-white/8 rounded-xl p-4 flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CreditCard className="w-3.5 h-3.5 text-gray-500" />
                <span className="text-[11px] uppercase tracking-[0.12em] text-gray-500">PAN Verification</span>
              </div>
              {panVerified
                ? <span className="flex items-center gap-1 text-[10px] text-emerald-400"><CheckCircle2 className="w-3 h-3" />Verified</span>
                : <span className="flex items-center gap-1 text-[10px] text-amber-400"><ShieldAlert className="w-3 h-3" />Pending</span>
              }
            </div>
            <div className="font-mono text-sm text-gray-300 tracking-wider">{profile?.pan_number || "—"}</div>
            {!panVerified && kycStatus?.has_pan && (
              <button onClick={handleVerifyPAN} disabled={verifyingPan} data-testid="partner-verify-pan-btn"
                className="mt-auto flex items-center justify-center gap-2 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] text-[12px] font-medium rounded-lg hover:bg-[#D4AF37]/20 disabled:opacity-50 transition-all">
                {verifyingPan ? <><Loader2 className="w-3 h-3 animate-spin" />Verifying…</> : "Verify PAN"}
              </button>
            )}
            {panVerified && (
              <div className="flex items-center gap-1.5 text-[11px] text-emerald-400 mt-auto">
                <CheckCircle2 className="w-3 h-3" />PAN successfully verified
              </div>
            )}
          </div>

          {/* Bank */}
          <div className="bg-white/3 border border-white/8 rounded-xl p-4 flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-3.5 h-3.5 text-gray-500" />
                <span className="text-[11px] uppercase tracking-[0.12em] text-gray-500">Bank Verification</span>
              </div>
              {bankVerified
                ? <span className="flex items-center gap-1 text-[10px] text-emerald-400"><CheckCircle2 className="w-3 h-3" />Verified</span>
                : <span className="flex items-center gap-1 text-[10px] text-amber-400"><ShieldAlert className="w-3 h-3" />Pending</span>
              }
            </div>
            <div className="font-mono text-sm text-gray-300 tracking-wider">
              {kycStatus?.bank_account_masked || "****"}&nbsp;&nbsp;{kycStatus?.bank_ifsc || ""}
            </div>
            {!bankVerified && kycStatus?.has_bank && (
              <button onClick={handleVerifyBank} disabled={verifyingBank} data-testid="partner-verify-bank-btn"
                className="mt-auto flex items-center justify-center gap-2 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] text-[12px] font-medium rounded-lg hover:bg-[#D4AF37]/20 disabled:opacity-50 transition-all">
                {verifyingBank ? <><Loader2 className="w-3 h-3 animate-spin" />Verifying…</> : "Verify Bank Account"}
              </button>
            )}
            {bankVerified && (
              <div className="flex items-center gap-1.5 text-[11px] text-emerald-400 mt-auto">
                <CheckCircle2 className="w-3 h-3" />Bank account successfully verified
              </div>
            )}
            {!kycStatus?.has_bank && !bankVerified && (
              <div className="text-[11px] text-gray-600 mt-auto">Update your bank details in Edit mode first</div>
            )}
          </div>
        </div>

        {overallKycDone && (
          <div className="mt-4 flex items-center gap-2 px-4 py-3 bg-emerald-500/8 border border-emerald-500/20 rounded-xl text-[12px] text-emerald-400">
            <ShieldCheck className="w-4 h-4 shrink-0" />
            KYC complete — both PAN and Bank account verified successfully.
          </div>
        )}

        {kycAdminReview && (
          <div className="mt-4 flex items-start gap-3 px-4 py-3.5 bg-amber-500/8 border border-amber-500/20 rounded-xl text-[12px] text-amber-300" data-testid="kyc-review-banner">
            <Clock className="w-4 h-4 shrink-0 mt-0.5 text-amber-400" />
            <div>
              <div className="font-semibold text-amber-400 mb-0.5">KYC Under Manual Review</div>
              <div className="text-amber-300/80 leading-relaxed">
                {kycStatus?.kyc_review_reason || "Your application is being reviewed by our team. We'll notify you once a decision is made."}
              </div>
              {kycStatus?.kyc_review_score != null && (
                <div className="mt-1.5 text-[11px] text-amber-400/60">
                  Name similarity score: {Math.round(kycStatus.kyc_review_score * 100)}%
                </div>
              )}
            </div>
          </div>
        )}

        {kycRejected && (
          <div className="mt-4 flex items-start gap-3 px-4 py-3.5 bg-red-500/8 border border-red-500/25 rounded-xl text-[12px] text-red-300" data-testid="kyc-rejected-banner">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-red-400" />
            <div>
              <div className="font-semibold text-red-400 mb-0.5">KYC Application Rejected</div>
              <div className="text-red-300/80 leading-relaxed">
                {kycStatus?.kyc_reject_reason || "Your KYC application has been rejected. Please contact support for assistance."}
              </div>
              {kycStatus?.kyc_reject_score != null && (
                <div className="mt-1.5 text-[11px] text-red-400/60">
                  Name similarity score: {Math.round(kycStatus.kyc_reject_score * 100)}%
                </div>
              )}
              <div className="mt-2 text-[11px] text-red-400/70">
                Please contact support or update your PAN / bank details and reapply.
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3">
        {editing ? (
          <>
            <button onClick={handleSave} disabled={saving} data-testid="save-profile-btn"
              className="px-5 py-2 bg-[#D4AF37] text-black text-sm font-semibold rounded-lg hover:bg-[#c9a42e] disabled:opacity-50 transition-colors">
              {saving ? "Saving..." : "Save Changes"}
            </button>
            <button onClick={() => setEditing(false)}
              className="px-5 py-2 bg-white/5 text-gray-400 text-sm rounded-lg hover:bg-white/10 transition-colors">
              Cancel
            </button>
          </>
        ) : (
          <button onClick={() => setEditing(true)} data-testid="edit-profile-btn"
            className="px-5 py-2 bg-white/5 text-gray-300 text-sm rounded-lg hover:bg-white/10 border border-white/10 transition-colors">
            Edit Details
          </button>
        )}
      </div>
    </div>
  );
}
